import {
  Products,
  ApplicationStatuses,
  ApplicantTypes,
  RateTypes,
  ConsentTypes,
  Programs,
  SurveyQuestionTypes,
  CitizenshipStatusTypes,
  ItinAccessTypes,
  Flows,
  RepaymentTerms,
  ConsentServiceEntityTypes,
  Countries,
  AddressTypes,
  MembershipStatusTypes,
  NationalIdNumberTypes,
	SchoolTypes,
} from "@/config/enums";

export const localStorageKeys = {
  LenderId: "slc-lid",
  LenderName: "lender-name",
  LenderPrimaryColor: "primary-color",
  LenderSecondaryColor: "secondary-color",
  LenderData: "lenderData",
  LeadSourceId: "leadSourceId",
  Product: "product",
  Program: "program",
  appInProgress: "appInProgress",
  currentApplicant: "current-applicant",
  CachedPath: "cache.path",
  FlowType: "flowType",
} as const;

export const resetKeyIds = [
  localStorageKeys.Product,
  localStorageKeys.LeadSourceId,
  localStorageKeys.Program,
  localStorageKeys.CachedPath,
  localStorageKeys.FlowType,
] as const;

export const storeGetterKeys = {
  PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT_DISPLAY: "PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT_DISPLAY",
  PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT: "PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT",
  PROGRAM_MINIMUM_LOAN_AMOUNT: "PROGRAM_MINIMUM_LOAN_AMOUNT",
  PROGRAM_MINIMUM_LOAN_AMOUNT_DISPLAY: "PROGRAM_MINIMUM_LOAN_AMOUNT_DISPLAY",
};

export class CountryIds {
  public static get USA(): number {
    return Countries.USA;
  }
}

export class AddressTypeIds {
  public static get PERMANENT(): number {
    return AddressTypes.PERMANENT;
  }
}

export class MembershipStatusTypeIds {
  public static get Stated(): number {
    return MembershipStatusTypes.Stated;
  }
  public static get StatedNotAMember(): number {
    return MembershipStatusTypes.StatedNotAMember;
  }
}

export class ProductIds {
  public static get LOC(): number {
    return Products.LOC;
  }
  public static get REFI(): number {
    return Products.REFI;
  }
  public static get PEL(): number {
    return Products.PEL;
  }
}

export class ProgramIds {
  public static get UG(): number {
    return Programs.UNDERGRADUATE;
  }
  public static get DG(): number {
    return Programs.DOMESTIC_GRADUATE;
  }
  public static get IG(): number {
    return Programs.INTERNATIONAL_GRADUATE;
  }
  public static get PR(): number {
    return Programs.PARENT;
  }
  public static get PARENT(): number {
    return Programs.PARENT;
  }
  public static get AD(): number {
    return Programs.ADVANCED_DEGREE;
  }
  public static get MD(): number {
    return Programs.MEDICAL;
  }
  public static get ST(): number {
    return Programs.STANDARD;
  }
  public static get IU(): number {
    return Programs.INTERNATIONAL_UNDERGRADUATE;
  }
  public static get DG2(): number {
    return Programs.DOMESTIC_GRADUATE2;
  }
  public static get BM(): number {
    return Programs.BIO_MEDICAL1;
  }
  public static get ET1(): number {
    return Programs.ENERGY_TRADE_SCHOOL1;
  }
  public static get ET2(): number {
    return Programs.ENERGY_TRADE_SCHOOL2;
  }
  public static get ET3(): number {
    return Programs.ENERGY_TRADE_SCHOOL3;
  }
  public static get AV1(): number {
    return Programs.AVIATION1;
  }
  public static get AV2(): number {
    return Programs.AVIATION2;
  }
  public static get AV3(): number {
    return Programs.AVIATION3;
  }
  public static get LANDSCAPING(): number {
    return Programs.LANDSCAPING;
  }
}

export class ApplicationStatusTypeIds {
  public static get NOT_SUBMITTED(): number {
    return ApplicationStatuses.NOT_SUBMITTED;
  }
  public static get EVALUATION(): number {
    return ApplicationStatuses.EVALUATION;
  }
  public static get SUBMITTED_AND_PROCESSING(): number {
    return ApplicationStatuses.SUBMITTED_AND_PROCESSING;
  }
  public static get COMPLETE(): number {
    return ApplicationStatuses.COMPLETE;
  }
  public static get DECLINED(): number {
    return ApplicationStatuses.DECLINED;
  }
  public static get CANCELLED(): number {
    return ApplicationStatuses.CANCELLED;
  }
  public static get SMR_DECLINED(): number {
    return ApplicationStatuses.SMR_DECLINED;
  }
  public static get RIGHT_TO_CANCEL(): number {
    return ApplicationStatuses.RIGHT_TO_CANCEL;
  }
}

export class ApplicantTypeIds {
  public static get PRIMARY_APPLICANT(): number {
    return ApplicantTypes.PRIMARY_APPLICANT;
  }

  public static get CO_APPLICANT(): number {
    return ApplicantTypes.CO_APPLICANT;
  }
  public static get STUDENT(): number {
    return ApplicantTypes.STUDENT;
  }
}

export class NationalIdNumberTypeIds {
  public static get SSN(): number {
    return NationalIdNumberTypes.SSN;
  }

  public static get ITIN(): number {
    return NationalIdNumberTypes.ITIN;
  }
}

export class RateTypeIds {
  public static get Fixed(): number {
    return RateTypes.Fixed;
  }

  public static get Variable(): number {
    return RateTypes.Variable;
  }
}

export class RepaymentTermIds {
  public static get TWENTY_YEAR(): number {
    return RepaymentTerms.TWENTY_YEAR;
  }

  public static get TWENTY_FIVE_YEAR(): number {
    return RepaymentTerms.TWENTY_FIVE_YEAR;
  }
}

export class ConsentTypeIds {
  public static get CREDIT(): number {
    return ConsentTypes.CREDIT;
  }

  public static get ECOMMUNICATION(): number {
    return ConsentTypes.ECOMMUNICATION;
  }

  public static get MOBILE(): number {
    return ConsentTypes.MOBILE;
  }

  public static get TERMS_AND_CONDITIONS(): number {
    return ConsentTypes.TERMS_AND_CONDITIONS;
  }

  public static get EMAIL_VALIDATION(): number {
    return ConsentTypes.EMAIL_VALIDATION;
  }

  public static get SOFT_PULL(): number {
    return ConsentTypes.SOFT_PULL;
  }
  public static get REFI_UNDERPAYMENT(): number {
    return ConsentTypes.REFI_UNDERPAYMENT;
  }
  public static get PARENT_ATTESTATION(): number {
    return ConsentTypes.PARENT_ATTESTATION;
  }
}

export class ConsentServiceEntityTypeIds {
  public static get Application(): number {
    return ConsentServiceEntityTypes.Application;
  }
  public static get Loan(): number {
    return ConsentServiceEntityTypes.Loan;
  }
  public static get Draw(): number {
    return ConsentServiceEntityTypes.Draw;
  }
}

export class SurveyQuestionTypeIds {
  public static get TwoYearsIncome(): number {
    return SurveyQuestionTypes.TwoYearsIncome;
  }
  public static get SelectedCreditRange(): number {
    return SurveyQuestionTypes.SelectedCreditRange;
  }
  public static get MaritalStatus(): number {
    return SurveyQuestionTypes.MaritalStatus;
  }
  public static get SMREstimateLoanAmount(): number {
    return SurveyQuestionTypes.SMREstimateLoanAmount;
  }
  public static get EmploymentStatus(): number {
    return SurveyQuestionTypes.EmploymentStatus;
  }
  public static get EmployerName(): number {
    return SurveyQuestionTypes.EmployerName;
  }
}

export class CitizenshipStatusTypeIds {
  public static get USCitizen(): number {
    return CitizenshipStatusTypes.USCitizen;
  }
  public static get PermanentResident(): number {
    return CitizenshipStatusTypes.PermanentResident;
  }
  public static get NonCitizen(): number {
    return CitizenshipStatusTypes.NonCitizen;
  }
  public static get NonUSCitizenWithSSN(): number {
    return CitizenshipStatusTypes.NonUSCitizenWithSSN;
  }
}

export class ItinAccessTypeIds {
  public static get None(): number {
    return ItinAccessTypes.None;
  }
  public static get BorrowerOnly(): number {
    return ItinAccessTypes.BorrowerOnly;
  }
  public static get CoborrowerOnly(): number {
    return ItinAccessTypes.CoborrowerOnly;
  }
  public static get BorrowerOrCoborrower(): number {
    return ItinAccessTypes.BorrowerOrCoborrower;
  }
  public static get BorrowerAndCoborrower(): number {
    return ItinAccessTypes.BorrowerAndCoborrower;
  }
}

export class EnrollmentStatusTypeIds {
  public static get FullTime(): number {
    return 1;
  }
  public static get HalfTime(): number {
    return 2;
  }
  public static get LessThanHalfTime(): number {
    return 3;
  }
}

export const coApplicantRoutes = [
  "coborrowerPersonalInfo",
  "coborrowerPersonalInfoContinued",
  "coborrowerRegistration",
  "coborrowerAdditionalInfo",
  "coborrowerHardPullConsent",
  "coborrowerInfo",
  "coborrowerInfoContinued",
  "coborrowerMaritalStatus",
  "coborrowerInvited",
  "coborrowerInvitedApplicantInfo",
  "coborrowerInvitedApplicantInfoContinued",
  "refiCosignerPersonalInfo",
  "refiCosignerPersonalInfoContinued",
  "refiCosignerInfo",
  "refiCosignerInfoContinued",
  "refiCosignerInvited",
  "refiCosignerInvitedApplicantInfo",
  "refiCosignerInvitedApplicantInfoContinued",
  "refiCosignerRegistration",
  "refiCosignerMaritalStatus",
  "refiCosignerUnderlyingLoansToRefinance",
  "refiCosignerAdditionalInfo",
  "refiCosignerHardPullConsent",
];

export enum QueryParameters {
  PRODUCT = "productTypeId",
  LID = "lid",
  LEAD_SOURCE = "leadSourceId",
  PROGRAM = "programTypeId",
  FLOW = "flowType",
}

/*******************************************************
	Programs that use the default UG States & Transitions
********************************************************/
export const ugPrograms: Readonly<number[]> = [ProgramIds.UG, ProgramIds.AV1];

/*******************************************************
	Programs that use the default DG States & Transitions
********************************************************/
export const dgPrograms: Readonly<number[]> = [ProgramIds.DG, ProgramIds.LANDSCAPING];

export const PersonTypes: Readonly<Record<string, number>> = {
  ["Primary Applicant"]: ApplicantTypeIds.PRIMARY_APPLICANT,
  ["Co-Applicant"]: ApplicantTypeIds.CO_APPLICANT,
  ["Student"]: ApplicantTypeIds.STUDENT,
};

export const dentalPrograms: Readonly<number[]> = [
  Number(Programs.DENTAL),
  Number(Programs.DENTAL2),
  Number(Programs.DENTAL3),
];

export const lawPrograms: Readonly<number[]> = [Number(Programs.LAW), Number(Programs.LAW2), Number(Programs.LAW3)];

export const medicalPrograms: Readonly<number[]> = [
  Number(Programs.MEDICAL),
  Number(Programs.MEDICAL2),
  Number(Programs.MEDICAL3),
  Number(Programs.MEDICAL_OSTEOPATHIC),
  Number(Programs.MEDICAL_OSTEOPATHIC2),
  Number(Programs.MEDICAL_OSTEOPATHIC3),
  Number(Programs.MEDICAL_AND_OSTEOPATHIC),
  Number(Programs.MEDICAL_AND_OSTEOPATHIC2),
  Number(Programs.MEDICAL_AND_OSTEOPATHIC3),
];

export const optometryPrograms: Readonly<number[]> = [
  Number(Programs.OPTOMETRY),
  Number(Programs.OPTOMETRY2),
  Number(Programs.OPTOMETRY3),
];

export const pharmacyPrograms: Readonly<number[]> = [
  Number(Programs.PHARMACY),
  Number(Programs.PHARMACY2),
  Number(Programs.PHARMACY3),
];

export const podiatryPrograms: Readonly<number[]> = [
  Number(Programs.PODIATRY),
  Number(Programs.PODIATRY2),
  Number(Programs.PODIATRY3),
];

export const theologyPrograms: Readonly<number[]> = [
  Number(Programs.THEOLOGY),
  Number(Programs.THEOLOGY2),
  Number(Programs.THEOLOGY3),
];

export const clinicalPsychologyPrograms: Readonly<number[]> = [
  Number(Programs.CLINICAL_PSYCHOLOGY),
  Number(Programs.CLINICAL_PSYCHOLOGY2),
  Number(Programs.CLINICAL_PSYCHOLOGY3),
];

export const veterinaryPrograms: Readonly<number[]> = [
  Number(Programs.VETERINARY_MEDICINE),
  Number(Programs.VETERINARY_MEDICINE2),
  Number(Programs.VETERINARY_MEDICINE3),
];

export const professionalGradFlowsDictionary: Readonly<Record<number, Readonly<number[]>>> = {
  [Flows.STRICT]: [Programs.DOMESTIC_GRADUATE, Programs.DOMESTIC_GRADUATE2],
  [Flows.MDDO]: medicalPrograms,
  [Flows.MEDICAL]: [
    Programs.DENTAL,
    Programs.DENTAL2,
    Programs.DENTAL3,
    Programs.PODIATRY,
    Programs.PODIATRY2,
    Programs.PODIATRY3,
    Programs.OPTOMETRY,
    Programs.OPTOMETRY2,
    Programs.OPTOMETRY3,
    Programs.VETERINARY_MEDICINE,
    Programs.VETERINARY_MEDICINE2,
    Programs.VETERINARY_MEDICINE3,
    Programs.HEALTH_PROFESSIONS,
    Programs.HEALTH_PROFESSIONS2,
    Programs.HEALTH_PROFESSIONS3,
  ],
  [Flows.PROF]: [
    Programs.LAW,
    Programs.LAW2,
    Programs.LAW3,
    Programs.THEOLOGY,
    Programs.THEOLOGY2,
    Programs.THEOLOGY3,
    Programs.PHARMACY,
    Programs.PHARMACY2,
    Programs.PHARMACY3,
    Programs.CLINICAL_PSYCHOLOGY,
    Programs.CLINICAL_PSYCHOLOGY2,
    Programs.CLINICAL_PSYCHOLOGY3,
    Programs.ADVANCED_DEGREE,
    Programs.ADVANCED_DEGREE2,
    Programs.ADVANCED_DEGREE3,
  ],
};

export const CustomSchoolTypes: Readonly<number[]> = [SchoolTypes.AVIATION];
